// Front-end status-poll client for guided Skipper Report generation.
// Consumes GET /api/v1/skipper/report/:id/status (session-auth). The 60-minute
// guardrail is enforced here from job_age_seconds — the backend persists no
// "stuck" flag (see .aidev/tasks/12537-guided-reports report-status-lifecycle plan).
import { getJSONData } from '@aytm/app/stats/lib/fetch'
import {
  TRACKING_RANK,
  claimGuidedReportTracking,
  releaseGuidedReportTracking
} from '@aytm/app/stats/stores/guidedReportTracking'

export const GENERATION_STATUS = {
  GENERATING: 'generating',
  READY: 'ready',
  ERRORED: 'errored'
} as const

export type GenerationStatus = 'generating' | 'ready' | 'errored' | null

export interface ReportStatusResponse {
  report_id: number
  status: GenerationStatus
  started_at: string | null
  job_age_seconds: number | null
  guardrail_seconds: number
}

// Poll cadence (the contract leaves the interval to the FE) and safety fallbacks.
export const POLL_INTERVAL_MS = 15000
export const GUARDRAIL_SECONDS_FALLBACK = 3600
// Backend cap is 3 in-flight per survey; the FE mirrors it to pre-disable Create.
export const MAX_INFLIGHT_REPORTS = 3

const statusUrl = (reportId: number): string => `/api/v1/skipper/report/${reportId}/status`

export const fetchReportStatus = ({
  reportId,
  signal
}: {
  reportId: number
  signal?: AbortSignal
}): Promise<ReportStatusResponse> => getJSONData({ url: statusUrl(reportId), signal })

export type PollStopReason = 'ready' | 'errored' | 'timeout' | 'untracked'

interface PollerCallbacks {
  onReady?: (reportId: number) => void
  onStop?: (reportId: number, reason: PollStopReason) => void
  onTick?: (status: ReportStatusResponse) => void
}

// Single-report poll loop. Stops on a terminal status, on the client-enforced
// guardrail (job_age_seconds >= guardrail_seconds), or on repeated request
// failure. stop() is idempotent and aborts any in-flight request. Never starts
// for a report another watcher already tracks — see guidedReportTracking.
export class GuidedReportStatusPoller {
  private static MAX_FAILURES = 3

  private timer: ReturnType<typeof setTimeout> | null = null
  private controller: AbortController | null = null
  private failures = 0
  private stopped = false
  private claimed = false

  constructor(
    private reportId: number,
    private callbacks: PollerCallbacks = {},
    private intervalMs: number = POLL_INTERVAL_MS
  ) {}

  // `claimed` covers the whole life of a started poller, so a second start() cannot open a
  // parallel loop — including while the first request is still in flight, when `timer` is null.
  start(): this {
    if (this.timer || this.stopped || this.claimed) return this
    // Outranked by the generation job poll, which carries the report the readback cannot. Refused
    // is as dormant as displaced, and says so: a host that kept a refused poller as a live one
    // would hold the report's only watcher slot with something that never polls.
    if (!claimGuidedReportTracking(this.reportId, { rank: TRACKING_RANK.status, stop: () => this.stop() })) {
      this.stop()
      return this
    }

    this.claimed = true
    this.poll()
    return this
  }

  // a displaced poller is dormant for good; the host drops it so the report can be watched again
  isStopped(): boolean {
    return this.stopped
  }

  stop(): void {
    this.stopped = true
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.controller?.abort()
    this.controller = null
    if (this.claimed) {
      releaseGuidedReportTracking(this.reportId)
      this.claimed = false
    }
  }

  private schedule(): void {
    if (!this.stopped) this.timer = setTimeout(() => this.poll(), this.intervalMs)
  }

  private finish(reason: PollStopReason): void {
    this.stop()
    if ('ready' == reason) this.callbacks.onReady?.(this.reportId)
    this.callbacks.onStop?.(this.reportId, reason)
  }

  private async poll(): Promise<void> {
    this.timer = null
    this.controller = new AbortController()

    try {
      const status = await fetchReportStatus({ reportId: this.reportId, signal: this.controller.signal })
      if (this.stopped) return
      this.failures = 0
      this.callbacks.onTick?.(status)

      const guardrail = status.guardrail_seconds || GUARDRAIL_SECONDS_FALLBACK
      if (null != status.job_age_seconds && status.job_age_seconds >= guardrail) return this.finish('timeout')

      switch (status.status) {
        case GENERATION_STATUS.READY:
          return this.finish('ready')
        case GENERATION_STATUS.ERRORED:
          return this.finish('errored')
        case GENERATION_STATUS.GENERATING:
          return this.schedule()
        default: // null / unknown -> not a tracked generation, stop quietly
          return this.finish('untracked')
      }
    } catch {
      if (this.stopped) return // AbortError from stop() lands here
      if (++this.failures >= GuidedReportStatusPoller.MAX_FAILURES) return this.finish('errored')
      this.schedule()
    }
  }
}
